// ============================================================
// ENTERING, LEAVING AND THE CHALLENGE (Squad Mode stage 6, ruled
// 2026-10-23) - who may enter Squad Mode, and what a turn start does to it.
//
// A brother enters with a Full Action, or by the COHESION CHALLENGE (a Free
// Action: a d10 at or under the current Cohesion; it costs nothing). What a
// failed Challenge does is a house rule: nothing (the default), or he enters
// at his next turn, which it uses. He needs another brother within the
// kill-team's support range ON HIS STAGE; with nobody there he cannot enter,
// and at his turn start he drops out with a line. The errata's free join
// stays open until the opener's next turn start.
//
// Distances come in as a function (the hook measures on the map); a
// distance the map cannot give (no positions) counts as in range: the table
// may play without the map (D22).
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant, KillTeamState } from '../roster/types';
import { isInTheFight } from '../roster/inTheFight';
import type { KillTeam } from './record';
import type { Pool } from './pool';

/** Why the Enter plate is dimmed: one sentence each, keyed (the drum's reasons, never a literal). */
export const KILL_TEAM_REASONS = {
  noOath: 'The kill-team has no Oath: choose one in the Team tab.',
  noLeader: 'The kill-team has no leader at the table: tick Leader on one sheet in the Team tab.',
  filling: "The Cohesion pool is not filled yet: the GM's screen fills it.",
  inSquad: 'Already in Squad Mode.',
  emptyPool: 'No Cohesion left.',
  alone: 'No other brother on this stage.',
  down: 'Out of the fight.',
  pending: 'Entering Squad Mode at his next turn.',
} as const;

/** The one reason with a number in it. */
export const nobodyInRange = (m: number) => `No brother within ${m} m.`;

/** Metres between two combatants, or null when the map cannot say. */
export type DistanceTo = (a: string, b: string) => number | null;

/** The other brothers on his stage within the support range (an unknown distance counts as in range, D22). */
export function supportReach(
  brotherId: string,
  others: readonly Combatant[],
  rangeM: number,
  distance: DistanceTo,
): Combatant[] {
  return others.filter((o) => {
    if (o.id === brotherId) return false;
    const d = distance(brotherId, o.id);
    return d === null || d <= rangeM;
  });
}

interface EnterCtx {
  brother: Combatant;
  kt: KillTeam;
  pool: Pool | null;
  /** The brothers on his stage, still in the fight (`brothersIn`), him among them. */
  here: readonly Combatant[];
  distance: DistanceTo;
}

/** What the kill-team itself lacks: an Oath, a pool (no leader here, or not filled yet), Cohesion left. */
function teamLacks(c: EnterCtx): string | null {
  if (!c.kt.oath) return KILL_TEAM_REASONS.noOath;
  if (!c.pool) return c.kt.leaderId ? KILL_TEAM_REASONS.filling : KILL_TEAM_REASONS.noLeader;
  return c.pool.pool <= 0 ? KILL_TEAM_REASONS.emptyPool : null;
}

/** Why he cannot enter Squad Mode now, or null when he can. */
export function cannotEnter(c: EnterCtx): string | null {
  const ks = c.brother.state.killTeam;
  if (ks?.pendingEntry) return KILL_TEAM_REASONS.pending;
  if (ks?.on) return KILL_TEAM_REASONS.inSquad;
  if (!isInTheFight(c.brother)) return KILL_TEAM_REASONS.down;
  const lacks = teamLacks(c);
  if (lacks) return lacks;
  if (!c.here.some((o) => o.id !== c.brother.id)) return KILL_TEAM_REASONS.alone;
  if (!supportReach(c.brother.id, c.here, c.kt.rangeM, c.distance).length) return nobodyInRange(c.kt.rangeM);
  return null;
}

/** The Cohesion Challenge: a d10 at or under the current Cohesion passes. */
export const challengePasses = (d10: number, pool: number): boolean => d10 <= pool;

/** In Squad Mode, freshly: nothing joined yet. */
export const entered = (): KillTeamState => ({ on: true });

interface TurnStart {
  /** His Squad Mode at his turn start (absent: Solo). */
  state: KillTeamState | undefined;
  name: string;
  /** Another brother in support range on his stage now. */
  supported: boolean;
  /** The table's ruling on a failed Challenge: does a pending entry still enter? */
  failureEnters: boolean;
}

/**
 * What his turn start does to Squad Mode: a pending entry enters (his turn spent on it) or, the rule since turned
 * off, lapses; out of support range he drops to Solo; the ability he used last turn is free again; one-shots end.
 * The state to write (undefined: Solo) and the lines to log.
 */
export function turnStartOutcome(t: TurnStart): { state: KillTeamState | undefined; lines: string[] } {
  const s = t.state;
  if (!s) return { state: undefined, lines: [] };
  if (s.pendingEntry) return pendingAtTurn(t);
  if (!t.supported)
    return { state: undefined, lines: [`${t.name} has no brother in support range: back to Solo Mode.`] };
  const joined = (s.joined ?? []).filter((j) => !j.oneShot);
  const { usedKey: _used, joined: _joined, ...rest } = s;
  return { state: { ...rest, ...(joined.length ? { joined } : {}) }, lines: [] };
}

function pendingAtTurn(t: TurnStart): { state: KillTeamState | undefined; lines: string[] } {
  if (!t.failureEnters) return { state: undefined, lines: [`${t.name}'s waiting entry into Squad Mode lapses.`] };
  if (!t.supported)
    return { state: undefined, lines: [`${t.name} cannot enter Squad Mode: no brother in support range.`] };
  return { state: entered(), lines: [`${t.name} enters Squad Mode; it takes his turn.`] };
}
